import React from "react";
import { Typography, Grid, Stack } from "@mui/material";
import StandardButton from "../../../../components/StandardButton";
import { AutoAwesome as AIIcon } from "@mui/icons-material";
import { useRecoilValue } from "recoil";
import { AIButton } from "../../../../components";
import Bugsnag from "@bugsnag/js";
import { remoteConfig } from "../../../../data/FirebaseApp";
import { getBoolean } from "firebase/remote-config";
import { useConfirm } from "material-ui-confirm";

import useRecipes from "../../../../hooks/useRecipes";
import organizationIdSelector from "../../../../atoms/organizationIdSelector";
import fileUploadLimitSelector from "../../../../atoms/fileUploadLimitSelector";
import RecipesDataGrid from "./RecipesDataGrid";
import RecipeDialog from "./RecipeDialog";
import RecipeGeneratorDialog from "../clients/RecipeGeneratorDialog";
import useEvents from "../../../../hooks/useEvents";
import EmptyRecipes from "./EmptyRecipes";
import NewColors from "../../../../themes/NewColors";

const EditRecipes = ({ onShowDetails }) => {
  const organizationId = useRecoilValue(organizationIdSelector);
  const fileUploadLimit = useRecoilValue(fileUploadLimitSelector);
  const events = useEvents();
  const confirm = useConfirm();
  const {
    recipes,
    recipesLoading,
    editRecipe,
    archiveRecipe,
    editIngredient,
    uploadImage,
    addRecipeFromObject,
  } = useRecipes({
    organizationId,
  });

  const isRecipeGeneratorEnabled = getBoolean(
    remoteConfig,
    "showRecipeGenerator",
  );

  const [showDialog, setShowDialog] = React.useState(false);
  const [showMacroSelectDialog, setShowMacroSelectDialog] =
    React.useState(false);

  const [selectedRecipe, setSelectedRecipe] = React.useState(null);

  return (
    <>
      {/* Show loading first, then empty state when data confirms no recipes */}
      {recipesLoading ? (
        <Typography>Loading recipes...</Typography>
      ) : !recipes || recipes.length === 0 ? (
        <EmptyRecipes
          onAddRecipe={() => {
            setSelectedRecipe(null);
            setShowDialog(true);
          }}
          onGenerateRecipe={
            isRecipeGeneratorEnabled
              ? () => {
                  setShowMacroSelectDialog(true);
                }
              : undefined
          }
          showAIGeneration={isRecipeGeneratorEnabled}
        />
      ) : (
        <>
          <Grid
            container
            style={{ marginTop: 0 }}
            justifyContent={"space-between"}
          >
            <Grid item xs={8}>
              <Typography variant="h6">Recipes</Typography>
              <Typography
                style={{
                  fontSize: 16,
                  fontWeight: "normal",
                  marginBottom: 12,
                }}
              >
                Enter recipe title, ingredients, and instructions here. These
                recipes will be included in meal plans for your clients.
              </Typography>
            </Grid>

            <Grid item xs={4}>
              <Stack direction={"row"} spacing={3} justifyContent={"flex-end"}>
                {isRecipeGeneratorEnabled && (
                  <AIButton
                    size="small"
                    variant="primary"
                    sx={{
                      background:
                        `linear-gradient(45deg, ${NewColors.active.primary}, ${NewColors.active.secondary}) !important`,
                      "&:hover": {
                        background:
                          `linear-gradient(45deg, ${NewColors.active.accent || NewColors.active.primary}, ${NewColors.active.secondary}) !important`,
                      },
                    }}
                    onClick={() => {
                      setShowMacroSelectDialog(true);
                      events.logButtonClick({
                        buttonName: "Generate Recipe",
                        pageName: "Nutrition",
                        tabName: "Recipes",
                      });
                    }}
                  >
                    Generate Recipe
                  </AIButton>
                )}
                <StandardButton
                  size="small"
                  variant="primary"
                  onClick={() => {
                    setSelectedRecipe(null);
                    setShowDialog(true);
                    events.logButtonClick({
                      buttonName: "Add New Recipe",
                      pageName: "Nutrition",
                      tabName: "Recipes",
                    });
                  }}
                >
                  Add Recipe
                </StandardButton>
              </Stack>
            </Grid>
          </Grid>

          <RecipesDataGrid
            recipes={recipes}
            onClick={(recipe) => {
              if (onShowDetails) {
                onShowDetails(recipe);
              }
              setSelectedRecipe(recipe);
            }}
            onEdit={(recipe) => {
              setSelectedRecipe(recipe);
              setShowDialog(true);
              events.logButtonClick({
                buttonName: "Edit Recipe",
                pageName: "Nutrition",
                tabName: "Recipes",
              });
            }}
            onUpdate={editRecipe}
            onDelete={(recipe) => {
              confirm({
                title: "Archive this recipe?",
                description: "This will archive the recipe. Continue?",
              })
                .then(async () => {
                  await archiveRecipe(recipe.id);
                  events.logButtonClick({
                    buttonName: "Delete Recipe",
                    pageName: "Nutrition",
                    tabName: "Recipes",
                  });
                })
                .catch(() => {
                  // User cancelled the confirmation - no action needed
                });
            }}
          />
        </>
      )}

      {/* Dialogs - Always rendered regardless of empty state */}

      {showMacroSelectDialog === true && (
        <RecipeGeneratorDialog
          recipes={recipes}
          defaultMacros={{
            proteins: 0,
            carbs: 0,
            fats: 0,
          }}
          onDialogClosed={(recipe) => {
            if (recipe) {
              addRecipeFromObject(organizationId, recipe);
            }
            setShowMacroSelectDialog(false);
          }}
        />
      )}

      <RecipeDialog
        open={showDialog}
        recipe={selectedRecipe}
        editIngredient={editIngredient}
        onClose={async (recipe, imageURL) => {
          if (recipe) {
            console.log("EditRecipes: RecipeDialog onClose called with recipe", {
              id: recipe.id,
              name: recipe.name,
              mealType: recipe.mealType,
              isEditing: !!selectedRecipe,
              selectedRecipeId: selectedRecipe?.id,
              originalRecipeId: selectedRecipe?.id,
              recipeIdMatch: recipe.id === selectedRecipe?.id
            });
            
            // CRITICAL: Validate we have the correct recipe ID when editing
            if (selectedRecipe && selectedRecipe.id && recipe.id !== selectedRecipe.id) {
              console.error("EditRecipes: CRITICAL - Recipe ID mismatch!", {
                selectedRecipeId: selectedRecipe.id,
                savedRecipeId: recipe.id
              });
              alert(`Error: Recipe ID mismatch. Expected ${selectedRecipe.id} but got ${recipe.id}. This would create a duplicate.`);
              return; // Don't save if IDs don't match
            }
            
            try {
              // Determine if we're creating or editing based on whether selectedRecipe exists
              const isEditing = !!selectedRecipe && !!selectedRecipe.id;
              
              if (isEditing) {
                // Editing existing recipe - use editRecipe which validates document exists
                console.log("EditRecipes: Editing existing recipe", recipe.id);
                await editRecipe(recipe);
                console.log("EditRecipes: Successfully updated recipe", recipe.id);
              } else {
                // Creating new recipe - use addRecipeFromObject
                console.log("EditRecipes: Creating new recipe", recipe.id);
                await addRecipeFromObject(organizationId, recipe);
                console.log("EditRecipes: Successfully created recipe", recipe.id);
              }
              
              if (imageURL && imageURL.size < fileUploadLimit) {
                uploadImage({ image: imageURL, recipe });
              } else if (imageURL) {
                Bugsnag.notify("image too large");
              }
            } catch (error) {
              console.error("EditRecipes: Error saving recipe", error);
              // Show user-friendly error message
              const errorMessage = error.message || "Failed to save recipe. Please try again.";
              alert(`Error saving recipe: ${errorMessage}`);
              // Don't close dialog on error so user can fix and retry
              return;
            }
          }
          setShowDialog(false);
          // Clear selected recipe when dialog closes to ensure clean state for next open
          setSelectedRecipe(null);
        }}
      />
    </>
  );
};

export default EditRecipes;
